import { useEffect } from 'react'
import { motion, useAnimationControls } from 'framer-motion'
import { Heart } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'

interface WishlistHeartProps {
  active: boolean
  loading?: boolean
  onToggle: () => void
  /** Increment this to replay the save burst (only on add, not remove). */
  burst?: number
  className?: string
  label?: string
}

function HeartBurst() {
  return (
    <span className="pointer-events-none absolute inset-0 grid place-items-center">
      {Array.from({ length: 8 }, (_, i) => {
        const angle = (i / 8) * Math.PI * 2 - Math.PI / 2
        return (
          <motion.span
            key={i}
            className="absolute h-1 w-1 rounded-full bg-wd-orange"
            initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            animate={{
              opacity: 0,
              x: Math.cos(angle) * 22,
              y: Math.sin(angle) * 22,
              scale: 0,
            }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          />
        )
      })}
    </span>
  )
}

export function WishlistHeart({
  active,
  loading = false,
  onToggle,
  burst = 0,
  className,
  label,
}: WishlistHeartProps) {
  const reduced = useUIStore((s) => s.motionReduced)
  const controls = useAnimationControls()

  useEffect(() => {
    if (burst === 0 || reduced) return
    controls.start(
      { scale: [1, 1.3, 0.9, 1] },
      { duration: 0.45, ease: 'easeOut' }
    )
  }, [burst, reduced, controls])

  return (
    <button
      type="button"
      disabled={loading}
      onClick={onToggle}
      aria-pressed={active}
      aria-label={label ?? (active ? 'Remove from wishlist' : 'Add to wishlist')}
      className={cn(
        'relative grid h-10 w-10 place-items-center border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wd-orange disabled:cursor-not-allowed',
        active
          ? 'border-wd-orange bg-wd-orange/10 text-wd-orange'
          : 'border-ink/10 text-carbon/60 hover:border-wd-orange hover:text-wd-orange',
        loading && 'cursor-wait opacity-60',
        className
      )}
    >
      <motion.span animate={controls} className="inline-flex">
        <Heart className={cn('h-5 w-5', active && 'fill-current')} />
      </motion.span>
      {burst > 0 && !reduced && <HeartBurst key={burst} />}
    </button>
  )
}